/**
 * As estatísticas da vitrola — o `music_stats_section.dart`.
 *
 * Dois blocos por pessoa: os dez mais tocados de sempre e os cinco últimos
 * scrobbles. As três primeiras posições ganham pódio — moldura, medalha,
 * etiqueta e barra de proporção —, e da quarta em diante a linha é lisa.
 *
 * Os textos em inglês são os de lá, letra por letra: `"$displayName's Top 10"`,
 * `"$username's latest 5 scrobbles"`, `'No music stats yet'`, `'now'`,
 * `'heard'`, `'${m}m ago'`. O Flutter escreve assim, e traduzir aqui faria as
 * duas telas dizerem coisas diferentes.
 *
 * O que o navegador obrigou a mudar:
 *
 * - O `_Shimmer` do pódio varre uma faixa clara na diagonal com dois
 *   `Alignment` animados. Aqui é um `background-position` andando de uma ponta
 *   à outra — mesma faixa, mesmos 2,6 s, sem o quadro a quadro.
 * - As três medalhas do Flutter (`emoji_events`, `workspace_premium`,
 *   `military_tech`) viram `trofeu`, `medalha` e `estrela`: a casa já tinha
 *   duas das três, e a terceira em desenho novo seria um desenho a mais para
 *   dizer a mesma coisa.
 * - A linha do pódio lá tem 66 px de altura com 50 px de capa e 16 de respiro
 *   dentro: não cabe, e o Flutter deixa transbordar 2,5 px. Aqui a altura é
 *   mínima e a linha cresce o quanto precisa.
 * - O `NumberFormat.decimalPattern()` vira `toLocaleString(locale())`: o ponto
 *   de milhar do português, a vírgula do inglês, conforme a língua do app.
 */

import { Fragment, useState } from 'react';
import {
  diaEMes,
  lerData,
  locale,
  traduzir,
  usarEstatisticasDeMusica,
  usarSessao,
  type EstadoDeMusica,
  type FaixaMaisTocada,
  type NomeDoIcone,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import { OuvirJunto } from './OuvirJunto';
import './estatisticasDeMusica.css';

export function EstatisticasDeMusica() {
  const { pessoas, carregando } = usarEstatisticasDeMusica();
  const { casa } = usarSessao();
  const [aberta, setAberta] = useState<EstadoDeMusica | null>(null);

  // Ninguém vinculou o Last.fm: a seção some inteira, como o `SizedBox.shrink`.
  if (pessoas.length === 0) return null;

  const vazio = pessoas.every((p) => p.tops.length === 0 && p.recentes.length === 0);
  if (carregando && vazio) return <Esboco />;

  return (
    <section className="es" aria-label={traduzir('Estatísticas de música')}>
      {pessoas.map((gente, i) => (
        <Fragment key={gente.slug}>
          {i > 0 ? <span className="es-divisor" aria-hidden="true" /> : null}
          <Bloco
            nome={casa?.pessoas.find((p) => p.slug === gente.slug)?.nome ?? gente.usuario}
            usuario={gente.usuario}
            tops={gente.tops}
            recentes={gente.recentes}
            aoAbrir={setAberta}
          />
        </Fragment>
      ))}
      <OuvirJunto estado={aberta} aoFechar={() => setAberta(null)} />
    </section>
  );
}

/** O `_MusicStatsBlock`: os mais tocados e os recentes de uma pessoa só. */
function Bloco({
  nome,
  usuario,
  tops,
  recentes,
  aoAbrir,
}: {
  nome: string;
  usuario: string;
  tops: FaixaMaisTocada[];
  recentes: EstadoDeMusica[];
  aoAbrir: (estado: EstadoDeMusica) => void;
}) {
  const maisTocada = tops.reduce((maior, t) => Math.max(maior, t.playCount), 0);

  return (
    <div className="es-bloco">
      <Cabeca
        icone="podio"
        titulo={traduzir("{nome}'s Top 10", { nome })}
        legenda={traduzir('{usuario} · mais ouvidos de sempre', { usuario })}
      />
      {tops.length === 0 ? (
        <Vazio />
      ) : (
        <div className="es-lista">
          {tops.map((faixa, i) => (
            <Fragment key={`${faixa.rank}-${faixa.trackName}`}>
              {/* Depois de uma linha de pódio o fio respira 7 px de cada lado,
                  senão ele encosta na moldura. */}
              {i > 0 ? (
                <span
                  className={tops[i - 1].rank <= 3 ? 'es-fio folgado' : 'es-fio'}
                  aria-hidden="true"
                />
              ) : null}
              <LinhaTop
                faixa={faixa}
                maisTocada={maisTocada}
                aoAbrir={() => aoAbrir(comoEstado(faixa, usuario))}
              />
            </Fragment>
          ))}
        </div>
      )}

      <span className="es-divisor" aria-hidden="true" />

      <Cabeca
        icone="historico"
        titulo={traduzir('Ouvidos há pouco')}
        legenda={traduzir("{usuario}'s latest 5 scrobbles", { usuario })}
      />
      {recentes.length === 0 ? (
        <Vazio />
      ) : (
        <div className="es-lista">
          {recentes.map((estado, i) => (
            <Fragment key={`${estado.trackName}-${estado.timestamp ?? i}`}>
              {i > 0 ? <span className="es-fio" aria-hidden="true" /> : null}
              <LinhaRecente estado={estado} aoAbrir={() => aoAbrir(estado)} />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * O `_openTrack`: a folha de "ouvir junto" pede um `MusicStatus`, e a faixa
 * mais tocada não tem álbum nem hora. O `'No Album'` e o `isPlaying: false` são
 * os mesmos que o Flutter inventa ali.
 */
function comoEstado(faixa: FaixaMaisTocada, usuario: string): EstadoDeMusica {
  return {
    username: usuario,
    trackName: faixa.trackName,
    artistName: faixa.artistName,
    albumName: 'No Album',
    imageUrl: faixa.imageUrl,
    isPlaying: false,
    listenUrl: faixa.listenUrl,
  };
}

/** O `_SectionHeader`: o selo redondo, o título e a legenda em maiúsculas. */
function Cabeca({
  icone,
  titulo,
  legenda,
}: {
  icone: NomeDoIcone;
  titulo: string;
  legenda: string;
}) {
  return (
    <div className="es-cabeca">
      <span className="es-cabeca-selo" aria-hidden="true">
        <Icone nome={icone} tamanho={18} />
      </span>
      <span className="es-cabeca-textos">
        <h3 className="es-titulo">{titulo}</h3>
        <span className="es-legenda">{legenda.toUpperCase()}</span>
      </span>
    </div>
  );
}

/** O `_TopTrackRow`, com e sem pódio. */
function LinhaTop({
  faixa,
  maisTocada,
  aoAbrir,
}: {
  faixa: FaixaMaisTocada;
  maisTocada: number;
  aoAbrir: () => void;
}) {
  const podio = faixa.rank <= 3;
  // `clamp(0.08, 1.0)`: a décima faixa de quem ouve uma só o ano inteiro ainda
  // precisa de um traço para se ver que a barra existe.
  const parte = Math.min(1, Math.max(0.08, faixa.playCount / (maisTocada === 0 ? 1 : maisTocada)));

  return (
    <button
      className={podio ? `es-top podio p${faixa.rank}` : 'es-top'}
      type="button"
      onClick={aoAbrir}
    >
      {podio ? <span className="es-lustro" aria-hidden="true" /> : null}
      {faixa.rank === 1 ? (
        <span className="es-faisca" aria-hidden="true">
          <Icone nome="brilho" tamanho={13} />
        </span>
      ) : null}

      {podio ? (
        <span className="es-selo podio">
          <Icone nome={medalhaDo(faixa.rank)} tamanho={12} />
          <span>{faixa.rank}</span>
        </span>
      ) : (
        <span className="es-selo">{faixa.rank}</span>
      )}

      <Arte url={faixa.imageUrl} rank={faixa.rank} />

      <span className="es-textos">
        <span className="es-linha-nome">
          <span className="es-nome">{faixa.trackName}</span>
          {podio ? (
            <span className="es-etiqueta">
              <Icone nome={medalhaDo(faixa.rank)} tamanho={10} />
              {faixa.rank === 1 ? traduzir('#1 TOP') : `#${faixa.rank}`}
            </span>
          ) : null}
        </span>
        <span className="es-artista">{faixa.artistName}</span>
        {podio ? (
          <span className="es-barra" aria-hidden="true">
            <i style={{ ['--es-parte' as string]: `${(parte * 100).toFixed(1)}%` }} />
          </span>
        ) : null}
      </span>

      <span className="es-tocadas">
        <b>{faixa.playCount.toLocaleString(locale())}</b>
        <span>{faixa.playCount === 1 ? 'play' : 'plays'}</span>
      </span>
    </button>
  );
}

/** O `_RecentTrackRow`: capa, nome, artista e há quanto tempo. */
function LinhaRecente({ estado, aoAbrir }: { estado: EstadoDeMusica; aoAbrir: () => void }) {
  return (
    <button
      className={estado.isPlaying ? 'es-recente ao-vivo' : 'es-recente'}
      type="button"
      onClick={aoAbrir}
    >
      <Arte url={estado.imageUrl} />
      <span className="es-textos">
        <span className="es-linha-nome">
          <span className="es-nome">{estado.trackName}</span>
          {estado.isPlaying ? (
            <span className="es-vivo">
              <span className="es-vivo-ponto" aria-hidden="true" />
              {traduzir('LIVE')}
            </span>
          ) : null}
        </span>
        <span className="es-artista">{estado.artistName}</span>
      </span>
      <span className="es-quando">
        <Icone nome="relogio" tamanho={13} />
        {quandoFoi(estado)}
      </span>
    </button>
  );
}

/** O `_timeLabel`, degrau por degrau. */
function quandoFoi(estado: EstadoDeMusica): string {
  if (estado.isPlaying) return 'now';
  const quando = lerData(estado.timestamp);
  if (!quando) return 'heard';

  const segundos = Math.floor((Date.now() - quando.getTime()) / 1000);
  if (segundos < 60) return traduzir('Agora mesmo');
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos}m ago`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas}h ago`;
  const dias = Math.floor(horas / 24);
  if (dias < 7) return `${dias}d ago`;
  return diaEMes(quando);
}

/** O `_PodiumArtwork` e o `_TrackArtwork`: a mesma capa, com e sem moldura. */
function Arte({ url, rank }: { url?: string | null; rank?: number }) {
  const podio = rank !== undefined && rank <= 3;

  return (
    <span className={podio ? 'es-arte podio' : 'es-arte'}>
      {url ? (
        <img src={url} alt="" loading="lazy" />
      ) : (
        <span className="es-sem-capa" aria-hidden="true">
          <Icone nome="nota-musical" tamanho={21} />
        </span>
      )}
      {podio ? <span className="es-arte-vidro" aria-hidden="true" /> : null}
      {podio ? (
        <span className="es-canto" aria-hidden="true">
          <Icone nome={medalhaDo(rank)} tamanho={11} />
        </span>
      ) : null}
    </span>
  );
}

/** A medalha de cada posição, a mesma no selo, na etiqueta e no canto da capa. */
function medalhaDo(rank: number): NomeDoIcone {
  if (rank === 1) return 'trofeu';
  if (rank === 2) return 'medalha';
  return 'estrela';
}

/** O `_EmptyStats`: quem ainda não scrobblou nada. */
function Vazio() {
  return (
    <p className="es-vazio">
      <Icone nome="onda" tamanho={26} />
      {traduzir('No music stats yet')}
    </p>
  );
}

/**
 * O `_MusicStatsSkeleton`: quatro linhas cinzentas enquanto a primeira rodada
 * não volta. Não é o `Esqueleto` das peças da casa porque ali as barras são
 * todas iguais, e aqui cada linha tem capa, duas linhas de texto e a contagem.
 */
function Esboco() {
  return (
    <div className="es esboco" aria-hidden="true">
      <span className="esqueleto" style={{ width: 170, height: 18 }} />
      <div className="es-esboco-linhas">
        {Array.from({ length: 4 }, (_, i) => (
          <span className="es-esboco-linha" key={i}>
            <span className="esqueleto" style={{ width: 46, height: 46, borderRadius: 14 }} />
            <span className="es-esboco-textos">
              <span className="esqueleto" style={{ height: 13, borderRadius: 6 }} />
              <span className="esqueleto" style={{ width: 120, height: 11, borderRadius: 5 }} />
            </span>
            <span className="esqueleto" style={{ width: 46, height: 11, borderRadius: 5 }} />
          </span>
        ))}
      </div>
    </div>
  );
}
